/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import dompurify from 'dompurify';
import {
    ThemeProvider, Modal, ModalWrapper, ModalHeader, ModalContent
} from '@workboard/wobo-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components'
import KRService from '../../services/kr.service';
import './style.scss';
import MetricDataHeader from './metric-header';
import MeasureText from './measure-text';
import KRDataTable from './kr-data-table';
import useScroll from '../../../hooks/useScroll';
import LoaderComp from '../../../components/helper/loader';
import { updateKRConst } from '../../../constants/updateKR.constants';
// import { debug } from 'easy-peasy';

const StyledModal = styled(Modal)`
    ${ModalWrapper} {
        width: 900px;
    }
    ${ModalHeader} {
        padding: 10px 20px !important;
        h1 {
        font-size: 16px !important;
        }
    }
    ${ModalContent} {
          padding: 0px !important;
    }
`;

export type KRUpdateModalProps = {
id: string,
metricIds?: string,
metricTeamOpenVal?: string,
opened?: number,
parentMid?: string,
refSrc?: string,
cancelCallback?: any,
metric?: any;
}

type KRDataState = {
  isOpen: boolean,
  isScroll: boolean,
  metric: any,
  goal: any,
  offset: number,
  display_kr_confidence: boolean,
  goalId: string,
  metricId: string,
  isStretchEnabled: false,
  viaDueForUpdate?: boolean
}

const krService = new KRService();

const addPortalClass = () => {
    const element = document.getElementById('app-portal-container');
    if (element) {
        element.setAttribute('class', 'portal-krupdate-modal-wrapper');
    }
}

const removePortalClass = () => {
    const element = document.getElementById('app-portal-container');
    if (element) {
        element.removeAttribute('class');
    }
}

const initialState: KRDataState = {
    isOpen: false,
    isScroll: false,
    metric: {},
    goal: {},
    offset: 0,
    display_kr_confidence: false,
    goalId: '',
    metricId: '',
    isStretchEnabled: false,
};

let isProcesingRequest = false;

const KRUpdateModal = (props: KRUpdateModalProps) => {
    // WW-8253 its verified
    const {
        id, metricTeamOpenVal, opened, parentMid, refSrc,
        cancelCallback,
    } = props;
    let { metricIds } = props;
    if (metricIds === id) {
        metricIds = '';
    }
    const params: any = {
        id,
        parentMid,
        refSrc,
    };
    const metricUpdate:boolean = (metricIds && metricIds !== undefined);
    const metricIdValues = metricIds ? metricIds.split(',') : [];
    const metricFindKey = metricIdValues.indexOf(id);
    const metricIdsCount = metricIdValues.length;
    const { t } = useTranslation();

    let saveLabel = 'Save';
    if (metricUpdate) {
        saveLabel = (metricFindKey != (metricIdsCount - 1)) ? t('Save & Update Next') : t('Done');
    }

    const [krData, setKRData] = useState<KRDataState>(initialState);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { isOpen, metric = {}, goal = {} } = krData;
    const [metricData, setMetricData] = useState<any>([]);
    const targetMetric = ((metric.counting == 4 || metric.counting == 6)
    ? metric.disp_upper_limit : metric.target_raw)
    const lowerLimit = (metric.counting == 4) ? metric.disp_lower_limit : 0
    const stretchTargetMetric = krData.isStretchEnabled && metric.counting != 4 ? metric.stretch_target : 0
    const isStretchEnabled = krData.isStretchEnabled;
    const isRangeKr = metric.counting == 4 ? true : false
    const isScrollCheck = (data: any) => (data.length > 0 && data.length % 30 === 0);
    const dispatch = useDispatch();
    const apiCall = (p:any = {}) => {
        if (isProcesingRequest) {
            return;
        }
        isProcesingRequest = true;
        krService.getEditMetricData({ ...params, ...p })
            .then((response) => {
                // console.log('response', response);
                isProcesingRequest = false;
                if (!isLoading) {
                    removeLoader();
                }
                setIsLoading(false);
                if (Object.keys(response.data).length) {
                    const mData = response.data.metricData;
                    const mDataLength: number = mData.length;
                    let updateKRData: any = {};
                    if (Object.keys(krData.metric).length) {
                        updateKRData = {
                            ...krData,
                            isScroll: isScrollCheck(mData),
                            offset: mDataLength + krData.offset,
                            display_kr_confidence: response.data.display_kr_confidence,
                            isStretchEnabled: response.data.isStretchEnabled,
                            viaDueForUpdate: refSrc == 'DUE_KR' ? 1 : 0,
                        }
                        setKRData(updateKRData);
                        setMetricData(metricData.concat([...mData]));
                    } else {
                        updateKRData = {
                            ...krData,
                            isOpen: true,
                            isScroll: isScrollCheck(mData),
                            metric: response.data.metric,
                            goal: response.data.goal,
                            offset: mDataLength,
                            display_kr_confidence: response.data.display_kr_confidence,
                            isStretchEnabled: response.data.isStretchEnabled,
                            viaDueForUpdate: refSrc == 'DUE_KR' ? 1 : 0,
                        };
                        setKRData(updateKRData);
                        setMetricData(mData);
                    }
                    dispatch({
                        type: updateKRConst.INITIAL_UPDATE_KR_PROPS,
                        updateKRData
                    })
                }
                removeLoader();
            })
            .catch((err: any) => {
                removeLoader();
                console.log('err', err);
            });
    }

    const fetchingMoreData = () => {
        if (krData.offset && krData.isScroll) {
            setIsLoading(true);
            apiCall({ offset: krData.offset });
        }
    }

    const setScrollRef: any = useScroll(fetchingMoreData);

    const showLoader = () => showLoaderWidget(document.getElementById('content'));
    useEffect(() => {
        if (!krData.isOpen) {
            showLoader();
            addPortalClass();
            apiCall();
        }
    // eslint-disable-next-line
    }, [props]);

    const onCloseModal = () => {
        const metricConf = props?.metric?.confidence || '';
        const updateId = document.getElementById(`update-button-${id}`);
        const updateKRId = document.getElementById(`kr-row-${id}`);
        if (isLoading) return;
        removePortalClass();
        setKRData({ ...krData, ...initialState });
        setMetricData([]);
        if (typeof cancelCallback === 'function' && metricConf) {
            cancelCallback(metricConf);
        }
        setTimeout(() => {
            if (typeof updateId !== 'undefined' && updateId != null) {
                updateId.focus();
            } else if (typeof updateKRId !== 'undefined' && updateKRId != null) {
                updateKRId.focus();
            }
        })
    }

    const generateRandomString = () => {
        const { crypto } = window;
        const array = new Uint32Array(1);

        return crypto.getRandomValues(array);
    }

    const getAtMentionFromEditor = (ckeditor, selector) =>{
        const ids = [];
        const mentions = $(ckeditor).find(selector);
        if(mentions){
            $(mentions).each((index, mention) => {
                var id = $(mention).attr('data-user-id');
                if (ids.indexOf(id) === -1) {
                    ids.push(id);
                } 
            });
        }
        return ids;
    }

    const onSave = () => {
        if (isLoading) return;
        const mDataValue = [];
        const mDataId = [];
        const mComment = {};
        const mCreateAt = {};
        metricData.forEach((d: any) => {
            if (typeof d.dataChanged !== undefined && d.dataChanged) {
                mDataValue.push(d.m_data == undefined ? '' : d.m_data);
                mDataId.push(d.id);
                mComment[d.id] = (d.comment);
                mCreateAt[d.id] = d.create_at;
            }
        });

        if (mDataId.length) {
            const mentionTeam = {}, mentionUser = {};
            mDataId.forEach((item, index)=>{
                const ck_editor = $('#simple-editor-'+item).find(".ck-content");
                if (ck_editor) {
                    mentionTeam[item] = getAtMentionFromEditor(ck_editor, ".selectedTeam").join(",");
                    mentionUser[item] = getAtMentionFromEditor(ck_editor, ".selectedUser").join(",");
                }
            });

            const metricId = metric.id;
            let metricGoalId = metric.goal_id ? metric.goal_id : metric.cascade_goal_id;
            if (metricGoalId == null && metric.parent_goal_id != null) {
                metricGoalId = metric.parent_goal_id;
            }
            const postParams: any = {
                metricId,
                metricGoalId,
                comment: JSON.stringify(mComment),
                create_ats: JSON.stringify(mCreateAt),
                data: JSON.stringify(mDataValue),
                ids: JSON.stringify(mDataId),
                mentionTeam,
                mentionUser,
                viaDueForUpdate: krData.viaDueForUpdate,
            };
            if (metric.source == 5) {
                postParams.survey = 1;
            } else if (metric.source == 10) {
                postParams.datastream = 1;
            }
            showLoader();
            krService.saveMetricData(postParams)
                .then((response: any) => {
                    removeLoader();
                    const responseData = response.data;
                    const { progress, stop_update, progress_consider, displayHit, counting } = responseData;
                    const nextMetricId = typeof metricIdValues[metricFindKey + 1] !== undefined
                        ? metricIdValues[metricFindKey + 1]
                        : '';
                    if (nextMetricId === '' || nextMetricId === undefined) {
                        onCloseModal();
                    }
                    if (($('#goal-details-popup') as any).dialog('isOpen') && ($('#cascadeMetricDetails') as any).dialog('isOpen')) {
                        ($('#cascadeMetricDetails') as any).dialog('close');
                    }
                    validateKrLink();
                    const metricParams: any = {
                        metricGoalId,
                        metricId,
                        metricUpdate,
                        nextMetricId,
                        metricTeamOpen: (metricTeamOpenVal ? 1 : ''),
                        cascadeGoalId: metric.cascade_goal_id,
                    };
                    if (!window.wbConfig?.isCloseKeyResultEnabled
                        && stop_update == 1 && progress_consider == 1
                        && (Number(displayHit) || (Number(progress) >= 100 && counting != 3))) {
                        // check the achive value and target value
                        if (typeof openKRStopUpdateModal !== undefined) {
                            openKRStopUpdateModal({
                                metricId,
                                goalName: goal.goal,
                                source: 'goalDetails',
                                metricParams,
                            });
                        }
                    } else if (typeof metricDataSaveAfterAjax !== 'undefined') {
                      metricDataSaveAfterAjax(
                        undefined,
                        undefined,
                        metricParams
                      )
                    }
                    const goalParams :any = {};
                    goalParams.id = goal.id;
                    goalParams.metricId = metricId;
                    goalParams.reload = generateRandomString();
                    if ($('.react-modal-okr-popup-overlay').length > 0) {
                        viewGoalDetailsPage(undefined, goalParams);
                    }
                    if ($('#update-krs-view').length > 0) {
                        window.location.reload(); //WW-22186
                    }

                    // reload this week component if we are on this week calendar tab
                    if (document.getElementById('mywork-top')) {
                        ($(document) as any).trigger('init_this_week_top');
                    }
                })
                .catch((err: any) => {
                    removeLoader();
                    onCloseModal();
                    console.log('error', err);
                })
        } else {
            onCloseModal();
        }
    }

    const onChangeText = (dataId: string, val: any, field = 'm_data') => {
        const metricData1 = metricData.map((m: any) => {
            if (m.id == dataId && field && typeof m[field] !== undefined) {
                m.dataChanged = true;
                m[field] = val;
            }
            return m;
        });
        setMetricData(metricData1);
    }

    const confidenceData = useSelector((state:any) => state.metric);
    return (isOpen
  && (
      <ThemeProvider>
          <StyledModal
              className="portal-modal-krupdate modal-backdrop-krupdate"
              title={`${t('Update Key Results')}`}
              isOpen={isOpen}
              onDismiss={() => onCloseModal()}
              onSubmit={() => onSave()}
              submitLabel={t("Save")}
              disableScrollLock
              disableEnforceFocus
          >
              <div className="ScrollStyle" ref={setScrollRef}>
                  <MetricDataHeader
                      goal={goal.goal}
                      name={metric.name}
                      target={targetMetric}
                      showTarget={metric.counting != 5}
                      confidence={confidenceData ? confidenceData.confidence : []}
                      display_kr_confidence={krData.display_kr_confidence}
                      goalId={confidenceData ? confidenceData.goal_id : goal.id}
                      cascadeId={confidenceData && confidenceData.cascade_goal_id}
                      metricId={confidenceData ? confidenceData.id : metric.id}
                      stretchTarget={stretchTargetMetric}
                      isStretchEnabled={isStretchEnabled}
                      isRangeKr={isRangeKr}
                      lowerLimit = {lowerLimit}
                  />
                  <MeasureText {...metric} />
                  <div className="kr-data-table-wrapper">
                      <KRDataTable
                          rows={metricData}
                          metric={metric}
                          onChangeText={onChangeText}
                      />
                  </div>
                  { isLoading && <LoaderComp />}
              </div>
          </StyledModal>
      </ThemeProvider>
  ))
}

const mapStateToProps = state => state
export default connect(mapStateToProps)(KRUpdateModal);
